import axios from 'axios';

const api = axios.create({
  baseURL: window.location.origin + '/api',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('token');
    if (token) cfg.headers.Authorization = 'Bearer ' + token;
    return cfg;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateProfile: (data) => api.put('/auth/update-profile', data)
};

export const ticketAPI = {
  create: (data) => api.post('/tickets', data),
  getAll: (params) => api.get('/tickets', { params }),
  getOne: (id) => api.get('/tickets/' + id),
  assign: (id, data) => api.put('/tickets/' + id + '/assign', data),
  updateStatus: (id, data) => api.put('/tickets/' + id + '/status', data),
  escalate: (id, data) => api.put('/tickets/' + id + '/escalate', data),
  addComment: (id, data) => api.post('/tickets/' + id + '/comment', data),
  uploadFile: (id, formData) => api.post('/upload/ticket/' + id, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  publicCreate: (data) => api.post('/tickets/public', data),
  trackTicket: (data) => api.post('/tickets/track', data),
  delete: (id) => api.delete('/tickets/' + id)
};

export const userAPI = {
  create: (data) => api.post('/users', data),
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get('/users/' + id),
  update: (id, data) => api.put('/users/' + id, data),
  getTechnicians: () => api.get('/users/technicians'),
  delete: (id) => api.delete('/users/' + id),
  resetPassword: (id, data) => api.put('/users/' + id + '/reset-password', data),
  getSeniors: () => api.get('/users/seniors')
};

export const supportCallAPI = {
  create: (data) => api.post('/support-calls', data),
  list: (params) => api.get('/support-calls', { params }),
  stats: () => api.get('/support-calls/stats'),
  update: (id, data) => api.patch(`/support-calls/${id}`, data),
};

export const lcoPaymentAPI = {
  getSetup: () => api.get('/lco-payment/setup'),
  saveSetup: (data) => api.post('/lco-payment/setup', data),
  getAndroidQr: () => api.get('/lco-payment/android-qr'),
  getAndroidConfig: () => api.get('/lco-payment/android-config'),
  createLink: (data) => api.post('/lco-payment/create-link', data),
};

export const lcoFeaturesAPI = {
  getAll: () => api.get('/lco-features'),
  update: (lco_code, data) => api.put('/lco-features/'+lco_code, data),
};

export const lcoStaffAPI = {
  getAll: () => api.get('/lco-staff'),
  create: (data) => api.post('/lco-staff', data),
  update: (id, data) => api.put('/lco-staff/'+id, data),
  remove: (id) => api.delete('/lco-staff/'+id),
};

export const inventoryAPI = {
  getProducts: () => api.get('/inventory/products'),
  addProduct: (data) => api.post('/inventory/products', data),
  getStock: () => api.get('/inventory/stock'),
  getMovements: (params) => api.get('/inventory/movements', {params}),
  addMovement: (data) => api.post('/inventory/movement', data),
};

export const expenseAPI = {
  getAll: (params) => api.get('/expenses', {params}),
  getSources: () => api.get('/expenses/sources'),
  getPnl: (year) => api.get('/expenses/pnl', {params:{year}}),
  add: (data) => api.post('/expenses', data),
  update: (id, data) => api.put('/expenses/'+id, data),
  remove: (id) => api.delete('/expenses/'+id),
};

export const paymentAPI = {
  all: (params) => api.get('/payments/all', {params}),
  summary: () => api.get('/payments/summary'),
  syncStatus: () => api.get('/payments/sync-status'),
  collect: (data) => api.post('/payments/collect', data),
  editEntry: (collectionId, entryIndex, data) => api.put('/payments/collect/'+collectionId+'/'+entryIndex, data),
  history: (username, year) => api.get('/payments/history/'+username, {params:year?{year}:{}}),
};

export const lcoAPI = {
  profile: () => api.get('/lco/profile'),
  customers: (params) => api.get('/lco/customers', { params }),
  tickets: (params) => api.get('/lco/tickets', { params }),
  changePassword: (data) => api.post('/lco/change-password', data),
  createTicket: (data) => api.post('/lco/tickets', data),
  olts: () => api.get('/lco/olts'),
  oltOnts: (id) => api.get('/lco/olts/'+id+'/onts'),
  aiAnalyze: (data) => api.post('/lco/ai-analyze', data),
  aiReport: () => api.get('/lco/ai-report'),
  traffic: (switch_ip) => api.get('/lco/traffic/'+switch_ip),
};

export const reportAPI = {
  pendingByLco: (params={}) => api.get('/reports/pending-by-lco', {params}),
  bmsUsers: (sub_dist) => api.get('/reports/bms-users', { params: { sub_dist } }),
  bmsLookup: (username) => api.get('/reports/bms-lookup', { params: { username } }),
  bmsStatusChanges: (sub_dist) => api.get('/reports/bms-status-changes', { params: { sub_dist } }),
  dailySummary: (params) => api.get('/reports/daily-summary', { params }),
  technicianPerformance: (params) => api.get('/reports/technician-performance', { params }),
  pendingTickets: () => api.get('/reports/pending-tickets'),
  slaBreachReport: (params) => api.get('/reports/sla-breach', { params }),
  escalationReport: (params) => api.get('/reports/escalation-report', { params }),
  techWorkReport: (params) => api.get('/reports/tech-work-report', { params }),
};

export const networkAPI = {
  createDistribution: (data) => api.post('/network/distributions', data),
  getDistributions: () => api.get('/network/distributions'),
  deleteDistribution: (id) => api.delete('/network/distributions/' + id),
  createSubDistribution: (data) => api.post('/network/subdistributions', data),
  getSubDistributions: (params) => api.get('/network/subdistributions', { params }),
  deleteSubDistribution: (id) => api.delete('/network/subdistributions/' + id),
  createLCO: (data) => api.post('/network/lcos', data),
  getLCOs: (params) => api.get('/network/lcos', { params }),
  deleteLCO: (id) => api.delete('/network/lcos/' + id),
  updateLCO: (id, data) => api.put('/network/lcos/' + id, data),
  updateSubDistribution: (id, data) => api.put('/network/subdistributions/' + id, data),
  updateDistribution: (id, data) => api.put('/network/distributions/' + id, data),
  getPublicLCOs: (tenantCode) => api.get('/network/public/' + tenantCode + '/lcos')
};

export const leaveAPI = {
  getTechLCOs: (techId) => api.get('/leave/tech/' + techId + '/lcos'),
  markOnLeave: (techId, data) => api.post('/leave/tech/' + techId + '/on-leave', data),
  returnFromLeave: (techId) => api.post('/leave/tech/' + techId + '/return', {})
};

export const monitorReportAPI = {
  submit: (data) => api.post('/monitor-reports', data),
  getAll: (params) => api.get('/monitor-reports', { params }),
  getMonitors: () => api.get('/monitor-reports/monitors'),
  update: (id, data) => api.put('/monitor-reports/' + id, data)
};

export const crmAPI = {
  myStats: () => api.get('/crm/my-stats'),
  getLogs: (params) => api.get('/crm/logs', { params }),
  logCall: (data) => api.post('/crm/log', data),
  getFollowUps: () => api.get('/crm/follow-ups'),
  getStats: (params) => api.get('/crm/stats', { params }),
};

export default api;
